/**
 * Normalize a variant type name to its canonical label.
 * Maps legacy DB values like "Weight" / "Volume" to the standardized display labels.
 * Returns the original value unchanged if no mapping is found (safe pass-through).
 * @param {string} name
 * @returns {string}
 */
const VARIANT_TYPE_MAP = {
  weight: 'Kilograms (kg)', Weight: 'Kilograms (kg)', WEIGHT: 'Kilograms (kg)',
  volume: 'Liters (L)',     Volume: 'Liters (L)',     VOLUME: 'Liters (L)',
  pcs: 'Pieces (pcs)', Pcs: 'Pieces (pcs)', PCS: 'Pieces (pcs)',
  piece: 'Pieces (pcs)', pieces: 'Pieces (pcs)', Pieces: 'Pieces (pcs)', PIECES: 'Pieces (pcs)',
  size: 'Size', SIZE: 'Size',
  pack: 'Pack', PACK: 'Pack', packet: 'Pack', Packet: 'Pack', pkt: 'Pack',
};

export const formatIndianCurrency = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) return amount;
  return new Intl.NumberFormat('en-IN').format(amount);
};

export const normalizeVariantType = (name) => {
  if (!name || typeof name !== 'string') return name;
  const trimmed = name.trim();
  if (VARIANT_TYPE_MAP[trimmed]) return VARIANT_TYPE_MAP[trimmed];
  const lower = trimmed.toLowerCase();
  const found = Object.keys(VARIANT_TYPE_MAP).find(k => k.toLowerCase() === lower);
  return found ? VARIANT_TYPE_MAP[found] : trimmed;
};

export const formatUnit = (value, unitHint = "") => {
  if (value === null || value === undefined) return "";

  let valStr = value.toString().trim();
  let numericVal = parseFloat(valStr);
  let unit = unitHint.toLowerCase();

  // If value already contains units (e.g., "500g", "1kg", "1000ml")
  const unitMatch = valStr.match(/[a-zA-Z]+/);
  if (unitMatch) {
    unit = unitMatch[0].toLowerCase();
    numericVal = parseFloat(valStr.replace(unitMatch[0], "").trim());
  }

  // Weight logic
  if (unit === "g" || unit === "gram" || unit === "grams" || unit.includes("weight")) {
    if (numericVal >= 1000) {
      return `${numericVal / 1000} Kg`;
    } else {
      const unitLabel = numericVal === 1 ? "gram" : "grams";
      return `${numericVal} ${unitLabel}`;
    }
  }

  if (unit === "kg" || unit === "kilogram") {
    if (numericVal < 1) {
      const grams = numericVal * 1000;
      const unitLabel = grams === 1 ? "gram" : "grams";
      return `${grams} ${unitLabel}`;
    }
    return `${numericVal} Kg`;
  }

  // Volume logic
  if (unit === "ml" || unit === "milliliter" || unit.includes("volume")) {
    if (numericVal >= 1000) {
      return `${numericVal / 1000} Liter`;
    } else {
      return `${numericVal} ml`;
    }
  }

  if (unit === "l" || unit === "liter" || unit === "litre") {
    if (numericVal >= 1) {
      return `${numericVal} Liter`;
    } else {
      return `${numericVal * 1000} ml`;
    }
  }

  // Fallback if we couldn't identify the unit but have a hint from groupName/variantType
  const hintLower = unitHint.toLowerCase();
  
  // If the string does not contain numeric values, just return it as is.
  if (isNaN(numericVal)) return valStr;

  // New label hints: Kilograms (kg), Liters (L), Pieces (pcs)
  if (hintLower.includes("kilogram") || hintLower.includes("kg") || hintLower.includes("weight") || hintLower.includes("gram")) {
    if (numericVal >= 1000) return `${numericVal / 1000} Kg`;
    return `${numericVal} ${numericVal === 1 ? "gram" : "grams"}`;
  }
  if (hintLower.includes("liter") || hintLower.includes("litre") || hintLower.includes("volume") || hintLower.includes("ml")) {
    if (numericVal >= 1000) return `${numericVal / 1000} L`;
    return `${numericVal} ml`;
  }
  if (hintLower.includes("piece") || hintLower.includes("pcs")) {
    return `${numericVal} pcs`;
  }

  return valStr;
};

/**
 * Capitalize only the first letter of the string
 * @param {string} str 
 * @returns {string}
 */
/**
 * Capitalize only the first letter of the string.
 * Safely handles objects by converting to string first.
 * @param {any} str 
 * @returns {string}
 */
export const capitalizeFirst = (str) => {
  if (str === null || str === undefined) return "";
  
  let val = str;
  if (typeof val === 'object') {
    val = val.name || val.title || JSON.stringify(val);
  }
  
  // If the result of the object extraction is STILL an object, recurse once or stringify
  if (typeof val === 'object' && val !== null) {
    val = val.name || val.title || JSON.stringify(val);
  }

  const s = String(val);
  if (!s) return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/**
 * Ensures a value is safe to be rendered as a React child.
 * If it's an object, it tries to get .name or .title recursively.
 * @param {any} value 
 * @returns {string|any}
 */
export const renderSafeText = (value) => {
  if (value === null || value === undefined) return "";
  if (typeof value === 'string' || typeof value === 'number') return value;
  
  if (typeof value === 'object') {
    // If it's already a React element, return as is (rare in this context but safe)
    if (value.$$typeof) return value; 
    
    const extracted = value.name || value.title;
    if (extracted !== undefined && extracted !== null) {
      // Recurse for nested objects (e.g. name: { name: 'Foo' })
      if (typeof extracted === 'object') return renderSafeText(extracted);
      return extracted;
    }
    
    return JSON.stringify(value);
  }
  
  return String(value);
};

/**
 * Capitalize first letter of each word
 * @param {string} str 
 * @returns {string}
 */
export const toTitleCase = (str) => {
  if (!str) return "";
  return str.toString().toLowerCase().split(/\s+/).map(word => {
    return word.charAt(0).toUpperCase() + word.slice(1);
  }).join(' ');
};

/**
 * Formats a delivery time string (e.g., "30 mins" or "113-123 mins")
 * into hours if it's 60 minutes or more.
 * @param {string} timeStr 
 * @returns {string}
 */
export const formatDeliveryTime = (timeStr) => {
  if (!timeStr) return "";
  const s = String(timeStr).toLowerCase();

  // If it doesn't mention minutes, return as is
  if (!s.includes("min")) return timeStr;

  // Extract numbers (handles "113-123" or "30")
  const numbers = s.match(/\d+/g);
  if (!numbers || numbers.length === 0) return timeStr;

  const formatValue = (mins) => {
    const m = parseInt(mins);
    if (m < 60) return `${m} mins`;
    
    const hrs = m / 60;
    // If it's a whole number, don't show decimal
    if (hrs % 1 === 0) return `${hrs} hr${hrs > 1 ? 's' : ''}`;
    // Show 1 decimal place (e.g., 1.5 hrs)
    return `${hrs.toFixed(1)} hr${hrs > 1 ? 's' : ''}`;
  };

  if (numbers.length === 1) {
    return formatValue(numbers[0]);
  }

  if (numbers.length >= 2) {
    // It's a range like 113-123
    const startMins = parseInt(numbers[0]);
    const endMins = parseInt(numbers[1]);

    if (startMins < 60 && endMins < 60) return timeStr;

    const startHrs = (startMins / 60).toFixed(1).replace(/\.0$/, '');
    const endHrs = (endMins / 60).toFixed(1).replace(/\.0$/, '');

    return `${startHrs} - ${endHrs} hrs`;
  }

  return timeStr;
};

/**
 * Calculates price per unit (gram/ml) and returns a formatted string.
 * @param {number} price 
 * @param {string} value (e.g. "500g", "1 kg")
 * @returns {string|null}
 */
export const calculateUnitPrice = (price, value) => {
  if (!price || !value) return null;

  let valStr = value.toString().toLowerCase();
  const numMatch = valStr.match(/(\d+\.?\d*)/);
  if (!numMatch) return null;

  const numValue = parseFloat(numMatch[1]);
  let unit = "";

  if (valStr.includes("kg") || valStr.includes("kilo")) unit = "kg";
  else if (valStr.includes("gram") || valStr.includes("g")) unit = "g";
  else if (valStr.includes("ml")) unit = "ml";
  else if (valStr.includes("l") || valStr.includes("liter") || valStr.includes("litre")) unit = "l";

  if (!unit) return null;

  let unitPrice = 0;
  let displayUnit = unit;

  if (unit === "g") {
    unitPrice = price / (numValue / 1000);
    displayUnit = "kg"; // Usually show price per kg if in grams
  } else if (unit === "kg") {
    unitPrice = price / numValue;
    displayUnit = "kg";
  } else if (unit === "ml") {
    unitPrice = price / (numValue / 1000);
    displayUnit = "liter";
  } else if (unit === "l") {
    unitPrice = price / numValue;
    displayUnit = "liter";
  }

  const rounded = Math.round(unitPrice * 100) / 100;
  return `₹${rounded} / ${displayUnit}`;
};

/**
 * Formats a raw notification title/description (often containing DB ENUMs) into human readable text.
 * @param {string} text 
 * @returns {string}
 */
export const formatNotificationText = (text) => {
  if (!text) return "";
  let formatted = String(text);

  const returnStatusLabels = {
      RETURN_REQUESTED: "Return Requested",
      RETURN_APPROVED_BY_ADMIN: "Return Approved",
      RETURN_REJECTED_BY_ADMIN: "Return Rejected",
      RETURN_PICKUP_PENDING: "Return Pickup Pending",
      RETURN_PICKUP_ASSIGNED: "Delivery Partner Assigned",
      RETURN_STAFF_ACCEPTED: "Delivery Partner Accepted",
      RETURN_STAFF_ARRIVED_AT_CUSTOMER: "Delivery Partner Arrived",
      RETURN_REJECTED_BY_SELLER: "Return Rejected By Seller",
      RETURN_PICKUP_REJECTED: "Pickup Rejected",
      RETURN_PICKED_UP: "Return Picked Up",
      RETURN_IN_TRANSIT: "Return In Transit",
      RETURN_IN_TRANSIT_TO_SHOP: "In Transit To Shop",
      RETURN_ARRIVED_AT_SHOP: "Arrived At Shop",
      RETURN_DELIVERED_TO_SELLER: "Delivered To Seller",
      RETURN_DISPUTED: "Return Disputed",
      RETURN_COMPLETED: "Return Completed",
      REFUND_PENDING: "Refund Pending",
      REFUND_PROCESSING: "Refund Processing",
      REFUND_COMPLETED: "Refund Processed",
  };

  Object.keys(returnStatusLabels).forEach(key => {
      const regex = new RegExp(`\\b${key}\\b`, 'g');
      formatted = formatted.replace(regex, `'${returnStatusLabels[key]}'`);
  });
  
  return formatted.replace(/Return Update: '([^']+)'/g, "Return Update: $1");
};

/**
 * Finds all category IDs that match a given search query.
 * Matches exact name, contained substring, or word/token stems (e.g. "Mobiles" -> "Mobile & Tablets").
 * @param {string} query
 * @param {Array} categories
 * @returns {Array<string>} Array of category IDs
 */
export const findMatchingCategoryIds = (query, categories) => {
  if (!query || !categories || !Array.isArray(categories) || categories.length === 0) return [];
  const qStr = String(query).toLowerCase().trim();
  if (!qStr) return [];

  // 1. Exact match
  const exactMatches = categories.filter(c => c?.name && c.name.toLowerCase().trim() === qStr);
  if (exactMatches.length > 0) {
    return exactMatches.map(c => c._id || c.id).filter(Boolean);
  }

  // 2. Substring match
  const substringMatches = categories.filter(c => {
    const cName = (c?.name || '').toLowerCase().trim();
    if (!cName) return false;
    return cName.includes(qStr) || (qStr.length > 3 && qStr.includes(cName));
  });
  if (substringMatches.length > 0) {
    return substringMatches.map(c => c._id || c.id).filter(Boolean);
  }

  const stopwords = new Set(['and', 'for', 'the', 'in', 'of', 'with', '&']);
  const qWords = qStr.split(/\s+/).map(w => w.replace(/[^\w]/g, '')).filter(w => w.length > 0 && !stopwords.has(w));

  // 3. Token stem match
  const matchedIds = [];
  categories.forEach(c => {
    const cName = (c?.name || '').toLowerCase().trim();
    if (!cName) return;
    const cId = c._id || c.id;
    if (!cId) return;

    const cWords = cName.split(/\s+/).map(w => w.replace(/[^\w]/g, '')).filter(w => w.length > 0 && !stopwords.has(w));
    let matchFound = false;
    for (const qw of qWords) {
      const qwStem = (qw.length > 3 && qw.endsWith('s')) ? qw.slice(0, -1) : qw;
      for (const cw of cWords) {
        const cwStem = (cw.length > 3 && cw.endsWith('s')) ? cw.slice(0, -1) : cw;
        if (qwStem === cwStem) {
          matchFound = true;
          break;
        }
      }
      if (matchFound) break;
    }
    if (matchFound) {
      matchedIds.push(cId);
    }
  });

  return matchedIds;
};

